import React, { useState } from "react";
import * as Mui from "@mui/material";
import EditLocationAltOutlined from "@mui/icons-material/EditLocationAltOutlined";
import { useForm } from "react-hook-form";
import { useSelector } from "react-redux";
import { siteSettingsEditToastMessage } from "services/constants";
import { showToast } from "services/utils/Status";
import sessionHandling from "services/utils/notificationUtils";
import { designColors } from "services/constants/designTokens";
import {
  GlobalFieldLabel,
  globalTextFieldSx,
} from "components/DesignSystem/sharedUiPatterns";
import { GlobalSidePanel } from "components/DesignSystem/GlobalSidePanel";
import { RequiredMark } from "components/DesignSystem/SettingsPageShell";
import { PanelActionFooter } from "components/CustomComponents/DesignSystemButtons";
import { PanelIconHeader } from "../settingsUi";

const FORM_ID = "geo-fenced-site-form";
const TAGS = ["Home", "Office", "Site"];

// designTheme keeps MUI's default blue primary, so focus rings and the
// selected radio are set to the app's teal here.
const fieldSx = {
  ...globalTextFieldSx,
  "& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline": {
    borderColor: designColors.tealDark,
  },
};
const radioSx = { "&.Mui-checked": { color: designColors.tealDark } };

const digitsOnly = (event) => {
  const charCode = event.charCode;
  if (charCode < 48 || charCode > 57) {
    event.preventDefault();
  }
};
const digitsAndPoint = (event) => {
  const charCode = event.charCode;
  if ((charCode < 48 || charCode > 57) && charCode !== 46) {
    event.preventDefault();
  }
};

// One labelled input of the panel.
const PanelField = ({ label, helper, error, children }) => (
  <Mui.Box>
    <GlobalFieldLabel helperText={helper}>
      {label}
      <RequiredMark />
    </GlobalFieldLabel>
    {children}
    {error && (
      <Mui.FormHelperText error sx={{ mt: 0.75, fontSize: 12.5 }}>
        {error}
      </Mui.FormHelperText>
    )}
  </Mui.Box>
);

// Mounted only while the panel is open, so the fields always start from the
// site that was opened.
const SiteEditForm = ({ site, setSaving, onSaved, onClose }) => {
  const { domain, token } = useSelector((state) => state.tokenReducer);
  const {
    register,
    formState: { errors },
    handleSubmit,
    trigger,
  } = useForm({
    mode: "onBlur",
    defaultValues: {
      location_name: site.location_name ?? "",
      address: site.address ?? "",
      city: site.city ?? "",
      radius: site.radius ?? "",
      pincode: site.pincode ?? "",
    },
  });
  const [tag, setTag] = useState(site.tag ?? "Home");

  const onSubmit = async (data) => {
    setSaving(true);
    try {
      const response = await fetch(`${domain}edit_location/`, {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
          Authorization: `token ${token}`,
        },
        body: JSON.stringify({
          location_id: site.id,
          location_status: null,
          city: data.city,
          location_name: data.location_name,
          address: data.address,
          radius: data.radius,
          pincode: data.pincode,
          tag,
        }),
      });
      await response.json();
      if (response.ok) {
        showToast("success", siteSettingsEditToastMessage.editMessage);
        onSaved();
        onClose();
      } else if (response.status === 409) {
        sessionHandling();
      } else {
        throw new Error("REQUEST_FAILED");
      }
    } catch (error) {
      console.error("[Index] operation failed:", error);
      showToast("error", "Couldn't save the site details. Please try again.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <form id={FORM_ID} onSubmit={handleSubmit(onSubmit)} noValidate>
      <Mui.Stack spacing={2.5}>
        <PanelField
          label="Site Name"
          helper="Enter a recognisable name for this location to help you easily identify it."
          error={errors.location_name?.message}
        >
          <Mui.TextField
            fullWidth
            size="small"
            placeholder="Enter location name"
            {...register("location_name", {
              required: "Location name is required",
              maxLength: {
                value: 30,
                message: "Maximum length is 30 characters",
              },
              minLength: {
                value: 3,
                message: "Minimum length is 3 characters",
              },
              validate: {
                nameAvailable: async (fieldValue) => {
                  if (fieldValue.trim().length < 3) {
                    return "Site name must contain at least one alphabet character.";
                  } else if (site.location_name !== fieldValue) {
                    const response = await fetch(
                      `${domain}check-location-name?location_name=${fieldValue.trim()}`,
                      {
                        method: "GET",
                        headers: {
                          "content-type": "application/json",
                          Authorization: `token ${token}`,
                        },
                      },
                    );
                    const resp = await response.json();
                    if (resp.exists) {
                      return "Site name already exists";
                    }
                  }
                },
                noDigitsOrSpecial: (fieldValue) => {
                  if (!/[A-Za-z]/.test(fieldValue)) {
                    return "Site name must contain at least one alphabet character.";
                  }
                  if (/^\d+$/.test(fieldValue)) {
                    return "Site name should not contain only digits.";
                  }
                  if (!/^[A-Za-z0-9\s&-]+$/.test(fieldValue)) {
                    return "Site name should only contain alphabets, spaces and numbers";
                  }
                  return true;
                },
              },
            })}
            error={Boolean(errors.location_name)}
            onBlur={() => trigger("location_name")}
            inputProps={{ "aria-label": "Site Name" }}
            sx={fieldSx}
          />
        </PanelField>

        <PanelField
          label="Address"
          helper="Provide the complete street address for the site. This ensures accurate mapping and geolocation services."
          error={errors.address?.message}
        >
          <Mui.TextField
            fullWidth
            size="small"
            placeholder="Enter address"
            {...register("address", {
              required: "Address is required",
            })}
            error={Boolean(errors.address)}
            inputProps={{ "aria-label": "Address" }}
            sx={fieldSx}
          />
        </PanelField>

        <PanelField
          label="City"
          helper="Type the city where the site is located. This helps in filtering and grouping your saved locations."
          error={errors.city?.message}
        >
          <Mui.TextField
            fullWidth
            size="small"
            placeholder="Enter city"
            {...register("city", {
              required: "City name is required",
              maxLength: {
                value: 32,
                message: "Maximum length is 32 characters",
              },
              minLength: {
                value: 3,
                message: "Minimum length is 3 characters",
              },
            })}
            error={Boolean(errors.city)}
            inputProps={{ maxLength: 38, "aria-label": "City" }}
            sx={fieldSx}
          />
        </PanelField>

        <Mui.Box
          sx={{
            display: "grid",
            gap: 2,
            gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" },
            alignItems: "start",
          }}
        >
          <PanelField
            label="Radius (m)"
            helper="Activities within this radius count as part of the site."
            error={errors.radius?.message}
          >
            <Mui.TextField
              fullWidth
              size="small"
              placeholder="Enter radius"
              {...register("radius", {
                required: "Radius is required",
                min: { value: 20, message: "Minimum radius is 20 meter" },
                max: { value: 100, message: "Maximum radius is 100 meter" },
                validate: {
                  companyCodeAvailable: async (fieldValue) => {
                    if (String(fieldValue).trim() !== "") {
                      if (parseInt(fieldValue) > 100) {
                        return "Maximum radius is 100 meter";
                      } else if (parseInt(fieldValue) < 19) {
                        return "Minimum radius is 20 meter";
                      } else {
                        return true;
                      }
                    }
                  },
                },
              })}
              error={Boolean(errors.radius)}
              onKeyPress={digitsAndPoint}
              inputProps={{ maxLength: 8, "aria-label": "Radius" }}
              sx={fieldSx}
            />
          </PanelField>

          <PanelField
            label="Pincode"
            helper="The site's postal code, for accurate identification."
            error={errors.pincode?.message}
          >
            <Mui.TextField
              fullWidth
              size="small"
              placeholder="Enter pincode"
              {...register("pincode", {
                required: "Pincode is required",
                minLength: {
                  value: 6,
                  message: "Minimum length is 6 numbers",
                },
                maxLength: {
                  value: 7,
                  message: "Maximum length is 7 numbers",
                },
              })}
              error={Boolean(errors.pincode)}
              onKeyPress={digitsOnly}
              inputProps={{ maxLength: 7, "aria-label": "Pincode" }}
              sx={fieldSx}
            />
          </PanelField>
        </Mui.Box>

        <Mui.Box>
          <GlobalFieldLabel>Tag</GlobalFieldLabel>
          <Mui.RadioGroup
            row
            name="site-tag"
            value={tag}
            onChange={(event) => setTag(event.target.value)}
          >
            {TAGS.map((option) => (
              <Mui.FormControlLabel
                key={option}
                value={option}
                label={option}
                control={<Mui.Radio size="small" sx={radioSx} />}
                sx={{
                  mr: 3,
                  "& .MuiFormControlLabel-label": {
                    fontSize: 14,
                    fontWeight: tag === option ? 600 : 400,
                    color:
                      tag === option
                        ? designColors.tealDark
                        : designColors.slate700,
                  },
                }}
              />
            ))}
          </Mui.RadioGroup>
        </Mui.Box>
      </Mui.Stack>
    </form>
  );
};

// Edit side panel for a geo-fenced site (replaces the old popup dialog).
export const SiteEditPanel = ({ open, site, onClose, onSaved }) => {
  const [saving, setSaving] = useState(false);

  return (
    <GlobalSidePanel
      open={open}
      onClose={onClose}
      header={
        <PanelIconHeader
          icon={<EditLocationAltOutlined />}
          title="Edit Site"
          subtitle="Update the details of this geo-fenced site."
        />
      }
      bodySx={{ px: { xs: 2, sm: 3 }, py: 2.5 }}
      footer={
        <PanelActionFooter
          onCancel={onClose}
          primaryLabel="Update"
          primaryProps={{ type: "submit", form: FORM_ID, loading: saving }}
        />
      }
    >
      {open && site && (
        <SiteEditForm
          key={site.id}
          site={site}
          setSaving={setSaving}
          onSaved={onSaved}
          onClose={onClose}
        />
      )}
    </GlobalSidePanel>
  );
};
